import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/** PLTR paths rebased to 100 at each anchor session (from ohlc_database_enriched.json). */
const CENTERED_PATH_DATA = [
  { t: -39, today: 106.67, mechanical: 123.49 },
  { t: -38, today: 105.01, mechanical: 124.0 },
  { t: -37, today: 107.57, mechanical: 114.76 },
  { t: -36, today: 108.15, mechanical: 113.66 },
  { t: -35, today: 107.44, mechanical: 110.77 },
  { t: -34, today: 106.74, mechanical: 108.14 },
  { t: -33, today: 102.07, mechanical: 104.16 },
  { t: -32, today: 104.96, mechanical: 107.8 },
  { t: -31, today: 105.16, mechanical: 116.28 },
  { t: -30, today: 106.07, mechanical: 120.44 },
  { t: -29, today: 106.16, mechanical: 112.98 },
  { t: -28, today: 107.64, mechanical: 105.6 },
  { t: -27, today: 107.86, mechanical: 109.75 },
  { t: -26, today: 107.43, mechanical: 110.09 },
  { t: -25, today: 107.21, mechanical: 111.54 },
  { t: -24, today: 104.0, mechanical: 117.64 },
  { t: -23, today: 112.5, mechanical: 115.01 },
  { t: -22, today: 122.86, mechanical: 111.37 },
  { t: -21, today: 126.09, mechanical: 112.47 },
  { t: -20, today: 119.43, mechanical: 120.19 },
  { t: -19, today: 111.61, mechanical: 118.49 },
  { t: -18, today: 111.22, mechanical: 99.83 },
  { t: -17, today: 106.37, mechanical: 93.47 },
  { t: -16, today: 107.11, mechanical: 88.89 },
  { t: -15, today: 103.66, mechanical: 88.46 },
  { t: -14, today: 102.2, mechanical: 100.34 },
  { t: -13, today: 102.88, mechanical: 97.29 },
  { t: -12, today: 100.46, mechanical: 100.51 },
  { t: -11, today: 105.73, mechanical: 103.31 },
  { t: -10, today: 104.58, mechanical: 102.29 },
  { t: -9, today: 102.53, mechanical: 96.18 },
  { t: -8, today: 100.83, mechanical: 92.96 },
  { t: -7, today: 93.79, mechanical: 94.23 },
  { t: -6, today: 91.59, mechanical: 93.64 },
  { t: -5, today: 89.08, mechanical: 98.81 },
  { t: -4, today: 84.19, mechanical: 100.68 },
  { t: -3, today: 88.64, mechanical: 96.61 },
  { t: -2, today: 90.79, mechanical: 89.06 },
  { t: -1, today: 91.57, mechanical: 91.35 },
  { t: 0, today: 100, mechanical: 100, mechanicalFwd: 100 },
  { t: 1, mechanicalFwd: 104.5 },
  { t: 2, mechanicalFwd: 108.74 },
  { t: 3, mechanicalFwd: 106.79 },
  { t: 4, mechanicalFwd: 112.55 },
  { t: 5, mechanicalFwd: 111.79 },
  { t: 6, mechanicalFwd: 113.57 },
  { t: 7, mechanicalFwd: 110.01 },
  { t: 8, mechanicalFwd: 113.83 },
  { t: 9, mechanicalFwd: 120.44 },
  { t: 10, mechanicalFwd: 117.73 },
  { t: 11, mechanicalFwd: 116.45 },
  { t: 12, mechanicalFwd: 117.3 },
  { t: 13, mechanicalFwd: 124.09 },
  { t: 14, mechanicalFwd: 117.81 },
  { t: 15, mechanicalFwd: 110.26 },
  { t: 16, mechanicalFwd: 108.91 },
  { t: 17, mechanicalFwd: 107.72 },
  { t: 18, mechanicalFwd: 107.38 },
  { t: 19, mechanicalFwd: 105.68 },
  { t: 20, mechanicalFwd: 109.41 },
  { t: 21, mechanicalFwd: 105.34 },
] as const;

type PathRow = {
  t: number;
  today?: number;
  mechanical?: number;
  mechanicalFwd?: number;
};

const CENTERED_PATH_ROWS: PathRow[] = CENTERED_PATH_DATA.map((row) => ({ ...row }));

const FINGERPRINT_METRICS = [
  { metric: '1-day', today: 9.21, mechanical: 9.47 },
  { metric: '20-day', today: -16.27, mechanical: -16.8 },
  { metric: 'From 20d low', today: 18.78, mechanical: 13.04 },
  { metric: 'From 20d high', today: -16.27, mechanical: -16.8 },
  { metric: 'RSI', today: 47.6, mechanical: 49.7 },
] as const;

const TODAY_COLOR = '#6366f1';
const MECHANICAL_COLOR = '#f87171';

const PATH_TICKS = [-39, -20, 0, 10, 21];

function formatRelativeDay(t: number) {
  if (t === 0) return 'T0';
  return t > 0 ? `T+${t}` : `T${t}`;
}

function PathTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; payload: PathRow }>;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const t = row.t;
  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 text-xs shadow-sm">
      <p className="mb-1 font-semibold">{formatRelativeDay(t)}</p>
      {row.today != null && (
        <p style={{ color: TODAY_COLOR }}>
          Today (Jul 1, 2026): {row.today.toFixed(1)} (rebased)
        </p>
      )}
      {row.mechanical != null && (
        <p style={{ color: MECHANICAL_COLOR }}>
          2022-03-16 setup: {row.mechanical.toFixed(1)} (rebased)
        </p>
      )}
      {row.mechanicalFwd != null && t > 0 && (
        <p style={{ color: MECHANICAL_COLOR }}>
          After 2022-03-16: {row.mechanicalFwd.toFixed(1)} ({(row.mechanicalFwd - 100).toFixed(1)}%)
        </p>
      )}
    </div>
  );
}

function FingerprintTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 text-xs shadow-sm">
      <p className="mb-1 font-semibold">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.dataKey === 'today' ? 'Jul 1, 2026' : 'Mar 16, 2022'}: {p.value > 0 ? '+' : ''}
          {p.value.toFixed(2)}{label === 'RSI' ? '' : '%'}
        </p>
      ))}
    </div>
  );
}

export function MechanicalAnalogDiagram() {
  return (
    <div className="space-y-6">
      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-semibold">40-session setup paths — anchored at the analog session (T0 = 100)</p>
          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-0.5 w-6 rounded" style={{ background: TODAY_COLOR }} />
              Today · Jul 1, 2026 (+9.21%)
            </span>
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block h-0.5 w-6 rounded border-t-2 border-dashed"
                style={{ borderColor: MECHANICAL_COLOR }}
              />
              Mechanical analog · Mar 16, 2022 (+9.47%)
            </span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={CENTERED_PATH_ROWS} margin={{ top: 12, right: 12, left: -8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
            <XAxis
              dataKey="t"
              type="number"
              domain={[-39, 21]}
              ticks={PATH_TICKS}
              tickFormatter={formatRelativeDay}
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={['auto', 'auto']}
              tickFormatter={(v) => `${v}`}
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip content={<PathTooltip />} />
            <ReferenceArea x1={0} x2={21} fill={MECHANICAL_COLOR} fillOpacity={0.04} />
            <ReferenceLine x={0} stroke="rgba(0,0,0,0.25)" strokeWidth={1.5} label={{ value: 'Analog session', position: 'top', fontSize: 10, fill: '#6b7280' }} />
            <ReferenceLine y={100} stroke="rgba(0,0,0,0.12)" strokeDasharray="4 3" />
            <Line
              type="monotone"
              dataKey="today"
              name="Today (Jul 1, 2026)"
              stroke={TODAY_COLOR}
              strokeWidth={2}
              dot={false}
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="mechanical"
              name="2022-03-16 setup"
              stroke={MECHANICAL_COLOR}
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={false}
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="mechanicalFwd"
              name="After 2022-03-16"
              stroke={MECHANICAL_COLOR}
              strokeWidth={2}
              dot={false}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          Left of T0: how each session arrived at its +9% pop. Right of T0: what PLTR did after the
          2022-03-16 mechanical match (+17.7% by T+10, then faded to +5.3% by T+21).
        </p>
      </div>

      <div>
        <p className="mb-3 text-sm font-semibold">Mechanical fingerprint at T0</p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={[...FINGERPRINT_METRICS]}
            barCategoryGap="28%"
            barGap={4}
            margin={{ top: 4, right: 8, left: -12, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
            <XAxis dataKey="metric" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis
              tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}${Math.abs(v) < 30 ? '' : ''}`}
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
            />
            <ReferenceLine y={0} stroke="rgba(0,0,0,0.2)" />
            <Tooltip content={<FingerprintTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Bar dataKey="today" name="Jul 1, 2026" fill={TODAY_COLOR} radius={[3, 3, 0, 0]} maxBarSize={32} />
            <Bar dataKey="mechanical" name="Mar 16, 2022" fill={MECHANICAL_COLOR} radius={[3, 3, 0, 0]} maxBarSize={32} />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-2 flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-sm" style={{ background: TODAY_COLOR }} />
            Today
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded-sm" style={{ background: MECHANICAL_COLOR }} />
            2022-03-16 mechanical analog
          </span>
        </div>
      </div>
    </div>
  );
}
